'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { X } from 'lucide-react';
import { ELEVENLABS_AGENT_IDS } from '@config/elevenlabs';
import { V4_BRAND } from '@/lib/marketing/messaging-bank/v4-brand';
import { PHONE_SHELL_CLASS, PHONE_SHELL_CSS } from '@/lib/widget/phone-shell';
import { SPARK_PRICE } from '@/lib/pricing/plans';
import { CONVERSION_EVENTS, pushEvent } from '@/lib/analytics/core';
import type { SessionMode } from '@/hooks/useVoiceWidget';
import type { FloatingVoiceHelperText, FloatingVoicePostCall, FloatingVoiceSurface } from './FloatingVoiceV1';
import './talk-to-bri-modal.css';

const FloatingVoiceV1 = dynamic(() => import('@/components/voice/FloatingVoiceV1'), {
  ssr: false,
});

/**
 * Every CTA on the homepage V5 surfaces (plus enterprise pricing) that can
 * open the shared conversation modal. Kept as an `Extract` over
 * `FloatingVoiceSurface` (rather than a hand-duplicated union) so the two
 * stay in sync — the modal's public `source` is always a value the widget
 * itself already knows how to attribute in demo_completed/demo_cta_clicked.
 */
export type TalkToBriSource = Extract<
  FloatingVoiceSurface,
  | 'navbar'
  | 'hero_fab'
  | 'roi'
  | 'final_cta'
  | 'orb'
  | 'sticky_bar'
  | 'enterprise_pricing'
  | 'hero_demo'
  | 'hero_phone'
>;

/** Ring mount size (px) — fixed per the mockup's design contract; also the
 *  explicit CLS-safe reserved box the ring mounts into. */
const RING_SIZE = 208;

// The platform's shared voice demo agent — the same one the homepage hero
// (HeroVoiceBridge) drives, so "Talk to Bri" always reaches the same
// receptionist persona regardless of which CTA opened the modal.
const DEFAULT_AGENT_ID = ELEVENLABS_AGENT_IDS.default;

const TITLE_ID = 'tbb-modal-title';

/**
 * Set on <body> for the lifetime of the modal. An explicit signal (rather
 * than inferring "modal open" from `document.body.style.overflow === 'hidden'`,
 * which this component also sets and which other code could set for
 * unrelated reasons) that other homepage chrome — e.g. MobileStickyVoiceBar —
 * reads to hide itself while the modal is up, per the "hidden while the
 * modal is open" contract.
 */
export const TBB_MODAL_OPEN_BODY_CLASS = 'tbb-modal-open';

export interface TalkToBriModalProps {
  source: TalkToBriSource;
  onClose: () => void;
  agentId?: string;
  /** Overrides the default Spark-CTA post-call card (e.g. enterprise
   *  pricing's `{ mode: 'restart' }`, which drops the self-serve CTA). */
  postCall?: FloatingVoicePostCall;
  /** Overrides the default idle/connecting/listening/speaking/ended copy. */
  helperText?: FloatingVoiceHelperText;
}

/**
 * How long to keep showing "Connecting you to Bri…" before deciding the
 * auto-start did not take and falling back to the tap hint. Generous enough to
 * cover the lazy-loaded widget chunk, the mic-permission prompt, and the signed
 * URL round trip; short enough that a genuinely failed start doesn't strand the
 * caller on a lie.
 */
const AUTO_START_GRACE_MS = 8000;

export default function TalkToBriModal({ source, onClose, agentId, postCall, helperText }: TalkToBriModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Tracks the widget's session mode so the pre-call subtitle can hide once
  // the caller leaves idle (connecting/listening/speaking/ended) — it reads
  // as stale advice ("tap the ring") once a call is underway or has ended.
  const [sessionMode, setSessionMode] = useState<SessionMode>('idle');
  // The modal auto-starts the session (see the `autoStart` prop below), so on
  // open we are connecting, not waiting for a tap. `sessionMode` alone can't
  // tell "auto-start still in flight" from "auto-start failed and reset us to
  // idle" — both read 'idle' — so the grace timer is what distinguishes them.
  // Nothing else needs tracking: the subtitle only renders while idle at all,
  // so a call that connects (or ends and resets to idle long after the grace
  // window) lands on the right copy without extra state.
  const [graceExpired, setGraceExpired] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setGraceExpired(true), AUTO_START_GRACE_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(async () => {
    onClose();
  }, [onClose]);

  // Esc to close + lock body scroll + focus management (basic focus trap).
  // Copied verbatim from EnterpriseVoiceModal.tsx — closing (including
  // mid-call) unmounts FloatingVoiceV1 below, which ends the ElevenLabs
  // session on cleanup.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add(TBB_MODAL_OPEN_BODY_CLASS);
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        void handleClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove(TBB_MODAL_OPEN_BODY_CLASS);
      previouslyFocused.current?.focus?.();
    };
  }, [handleClose]);

  // The post-call CTA card only renders after a clean `ended` state, but the
  // median demo runs ~29s and most callers close the modal (X / Esc / backdrop)
  // before Bri finishes — demo_initiated ~110/mo vs demo_completed ~9/mo in the
  // 30 days to 2026-08-14, so ~92% never see that register CTA. This persistent
  // link sits below the ring for the whole session so every demo engager has a
  // one-tap path to registration, not just the few who reach the post-call card.
  // Gated to self-serve demos: enterprise surfaces pass `{ mode: 'restart' }`
  // (no self-serve CTA) and must not show it. Attribution rides
  // V4_BRAND.postDemoCtaHref (plan + voice_demo utm); the gclid is carried
  // independently by Google's first-party _gcl_aw cookie.
  const selfServe = !postCall || postCall.mode === 'cta';

  // Portaled to document.body — several approved trigger surfaces (the
  // navbar chip in particular, whose `<nav>` sets `backdrop-filter`) sit
  // inside an ancestor that establishes a new containing block for
  // `position: fixed` descendants. Left in-tree, this overlay's "fixed"
  // positioning would resolve against that ancestor's box instead of the
  // viewport, squashing the modal into the navbar's small pill. Portaling
  // guarantees `.tbb-modal-overlay` is always a direct child of <body>,
  // regardless of where the trigger is mounted. Safe unconditionally: this
  // component is only ever loaded via `next/dynamic(..., { ssr: false })`,
  // so it never renders outside the browser.
  return createPortal(
    <div
      role="presentation"
      className={PHONE_SHELL_CLASS.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) void handleClose();
      }}
    >
      {/* The handset shell, from the same module `/widget.js` inlines onto
          third-party pages. Rendered here rather than imported as CSS so there
          is exactly one definition of the shell; scoped to the portal, so it
          unmounts with the modal and cannot accumulate across opens. */}
      <style>{PHONE_SHELL_CSS}</style>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        data-testid="tbb-modal"
        className={`tbb-modal ${PHONE_SHELL_CLASS.bezel}`}
      >
        {/* The handset screen. Everything below lives on the glass; the bezel
            stays a pure frame so its inset rings hold. */}
        <div className={PHONE_SHELL_CLASS.screen}>
          <div className={PHONE_SHELL_CLASS.island} aria-hidden="true" />
          <button
            type="button"
            onClick={() => void handleClose()}
            aria-label="Close"
            className={PHONE_SHELL_CLASS.close}
          >
            <X size={18} aria-hidden="true" />
          </button>

          <div className="tbb-modal-head">
            <span className="tbb-modal-live">
              <span className="tbb-dot" aria-hidden="true" />
              Live demo
            </span>
          </div>

          <h2 id={TITLE_ID} className="tbb-modal-title">
            Talk to <em>Bri</em>.
          </h2>
          {sessionMode === 'idle' &&
            (!graceExpired ? (
              <p className="tbb-modal-sub" data-testid="tbb-modal-connecting">
                Connecting you to Bri…
              </p>
            ) : (
              // Fallback only: auto-start was blocked (mic denied, autoplay policy,
              // a failed signed-URL fetch). The ring stays tappable in every one of
              // those cases — useVoiceWidget resets to 'idle' rather than wedging.
              <p className="tbb-modal-sub">Tap the ring — she picks up on the first ring.</p>
            ))}

          <div className={PHONE_SHELL_CLASS.stage}>
            {/* `minHeight`, not `height`: still the CLS-safe reserved box, but
                the post-call CTA card renders in here too and is far taller
                than the ring. Capping the height left that card spilling out
                of a fixed box — out of flow, so the screen's scroll height
                never grew and the tail of the card was unreachable. */}
            <div data-testid="tbb-ring-mount" style={{ width: `${RING_SIZE}px`, minHeight: `${RING_SIZE}px` }}>
              <FloatingVoiceV1
                surface={source}
                variant="modal-ring"
                agentId={agentId ?? DEFAULT_AGENT_ID}
                size={RING_SIZE}
                visualReveal="immediate"
                // Opening this modal IS the intent to talk — every trigger is an
                // explicit "Talk to Bri" CTA, so a second tap on the ring was pure
                // friction. Pass the boolean only: FloatingVoiceV1 derives its own
                // token from it and hands BOTH to the interactive layer, whose
                // auto-start effect consumes the two guards together. That shared
                // guard is what stops the duplicate-session "dual echo"; adding an
                // autoStartToken of our own here would be redundant, not safer.
                // This is the same path `?autostart=1` already drives in prod.
                autoStart
                onSessionModeChange={setSessionMode}
                helperText={
                  helperText ?? {
                    idle: 'Bri will ask about your business — just talk normally.',
                    connecting: 'Connecting...',
                    listening: 'Listening - go ahead.',
                    speaking: 'Bri is speaking...',
                    ended: 'Call ended. Tap to talk again.',
                  }
                }
                postCall={
                  postCall ?? {
                    mode: 'cta',
                    title: 'That was Bri — live, unscripted.',
                    body: "She'll answer your customers exactly like that, on the first ring, 24/7/365.",
                    href: V4_BRAND.postDemoCtaHref,
                    label: `Get started — ${SPARK_PRICE}`,
                    dismissLabel: '↻ Talk to her again',
                  }
                }
              />
            </div>
          </div>

          {selfServe && (
            <Link
              href={V4_BRAND.postDemoCtaHref}
              className="tbb-modal-persist-cta"
              data-testid="tbb-persist-register-cta"
              onClick={() =>
                pushEvent(CONVERSION_EVENTS.DEMO_CTA_CLICKED, { source, placement: 'persistent' })
              }
            >
              Create your account — {SPARK_PRICE}
              <span aria-hidden="true"> →</span>
            </Link>
          )}

          <p className="tbb-modal-powered">
            ● Powered by <b>BizRnR</b>
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
